import React, { useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { useModal } from '../ui/ModalProvider';

const TalentPoolPage = () => {
  const { showAlert } = useModal();
  const { roleId } = useParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    experience: '',
    portfolio: '',
    message: ''
  });

  const roleInfo = {
    'software-engineer': {
      title: 'Sacred Software Engineer',
      description: 'Join our engineering team to build conscious, scalable solutions that serve humanity\'s highest potential.',
      requirements: [
        'Strong programming skills in JavaScript, Python, or similar',
        'Experience with modern frameworks (React, Node.js, etc.)',
        'Understanding of software architecture and design patterns',
        'Conscious approach to problem-solving and innovation'
      ]
    },
    'product-designer': {
      title: 'Sacred Product Designer',
      description: 'Create beautiful, intuitive experiences that honor both functionality and spiritual consciousness.',
      requirements: [
        'Strong portfolio showcasing conscious UI/UX work',
        'Experience with design tools (Figma, Sketch, etc.)',
        'Understanding of user-centered design principles',
        'Ability to infuse design with spiritual awareness'
      ]
    }
  };

  const role = roleInfo[roleId] || {
    title: 'Sacred Custom Role',
    description: 'We\'re excited to hear about your unique sacred calling and how you can serve our mission.',
    requirements: [
      'Passion for conscious technology and innovation',
      'Strong problem-solving abilities with spiritual awareness',
      'Excellent communication skills and empathy',
      'Willingness to learn, grow, and serve humanity'
    ]
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    console.log('Application submitted:', formData);
    showAlert('Thank you for your application! We\'ll be in touch soon.');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="min-h-screen bg-black text-silver py-16"
    >
      <div className="max-w-4xl mx-auto px-4">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-px h-8 bg-silver/30"></div>
            <p className="text-xs font-mono tracking-[0.4em] text-silver/50 uppercase">
              Sacred Calling
            </p>
            <div className="w-px h-8 bg-silver/30"></div>
          </div>
          <h1 className="text-5xl md:text-7xl font-light mb-6 tracking-tight">{role.title}</h1>
          <p className="text-xl text-silver/70 mb-8 font-light">{role.description}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="bg-black/50 p-8 rounded-2xl border border-silver/10 backdrop-blur-sm"
          >
            <h2 className="text-2xl font-light mb-6 text-silver">Sacred Requirements</h2>
            <ul className="space-y-4">
              {role.requirements.map((req, index) => (
                <li key={index} className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-silver/60 rounded-full mr-4 mt-2 flex-shrink-0"></div>
                  <span className="text-silver/70 font-light">{req}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="bg-black/50 p-8 rounded-2xl border border-silver/10 backdrop-blur-sm"
          >
            <h2 className="text-2xl font-light mb-6 text-silver">Begin Your Sacred Journey</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:border-silver"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:border-silver"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Years of Experience</label>
                <input
                  type="number"
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:border-silver"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Portfolio/Resume URL</label>
                <input
                  type="url"
                  name="portfolio"
                  value={formData.portfolio}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:border-silver"
                  placeholder="https://your-portfolio.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Why should we hire you?</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:border-silver resize-none"
                  placeholder="Tell us about your passion for this role..."
                  required
                />
              </div>
              
                              <button
                  type="submit"
                  className="w-full bg-silver/10 text-silver border border-silver/30 py-4 px-6 rounded-xl font-light hover:bg-silver/20 hover:border-silver/50 transition-all duration-500"
                >
                  Submit Sacred Application
                </button>
            </form>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default TalentPoolPage;
